/**
 * InfraLeads · Admin quote-request inbox
 * ---------------------------------------------------------------------------
 * Every time a visitor clicks "REQUEST QUOTE" on the Hacking Infra page, a
 * document lands in `quote_requests` in Mongo. This panel is the owner's
 * inbox for those leads — status Kanban, quick-reply Telegram/email links,
 * inline status updates + delete.
 */
import React, { useEffect, useState } from 'react';
import { Loader2, RefreshCcw, Send, Mail, Trash2, ExternalLink, Phone, MessageCircle, Filter, DollarSign, User, Zap } from 'lucide-react';
import { toast } from 'sonner';
import { api } from '../../lib/api';

const STATUS = {
  new:        { label: 'NEW',        color: '#4de0ff', order: 1 },
  contacted:  { label: 'CONTACTED',  color: '#ffd34d', order: 2 },
  quoted:     { label: 'QUOTED',     color: '#c084fc', order: 3 },
  paid:       { label: 'PAID',       color: '#00ff9d', order: 4 },
  closed:     { label: 'CLOSED',     color: '#666',    order: 5 },
  spam:       { label: 'SPAM',       color: '#ff5078', order: 6 },
};

const CHANNEL_ICON = { telegram: Send, email: Mail, whatsapp: MessageCircle, phone: Phone };

const Section = ({ kicker, title, actions, children }) => (
  <div className="max-w-none">
    <div className="flex flex-wrap items-end justify-between gap-3 mb-5">
      <div>
        <div className="eh-kicker mb-1">{kicker}</div>
        <h2 className="eh-display text-2xl font-black">{title}</h2>
      </div>
      {actions}
    </div>
    {children}
  </div>
);

const timeAgo = (iso) => {
  if (!iso) return '—';
  const t = new Date(iso).getTime();
  if (Number.isNaN(t)) return '—';
  const s = Math.floor((Date.now() - t) / 1000);
  if (s < 60) return `${s}s`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h`;
  return `${Math.floor(h / 24)}d`;
};

const contactHref = (channel, contact) => {
  if (!contact) return null;
  const c = channel || 'email';
  if (c === 'telegram') return `https://t.me/${contact.replace(/^@/, '')}`;
  if (c === 'whatsapp') return `https://wa.me/${contact.replace(/[^\d+]/g, '')}`;
  if (c === 'phone')    return `tel:${contact.replace(/[^\d+]/g, '')}`;
  return `mailto:${contact}`;
};

const InfraLeads = () => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState('');
  const [selected, setSelected] = useState(null);
  const [busy, setBusy] = useState('');

  const refresh = async () => {
    setLoading(true);
    try {
      const r = await api.adminInfraQuotes(status);
      setRows(Array.isArray(r?.quotes) ? r.quotes : []);
    } catch (e) {
      toast.error(`Failed to load leads: ${e.message}`);
    } finally { setLoading(false); }
  };
  useEffect(() => { refresh(); /* eslint-disable-next-line */ }, [status]);

  const updateStatus = async (id, next) => {
    setBusy(id + ':' + next);
    try {
      await api.adminInfraQuoteUpdate(id, { status: next });
      toast.success(`Marked ${STATUS[next]?.label || next}`);
      await refresh();
      if (selected && selected._id === id) setSelected({ ...selected, status: next });
    } catch (e) {
      toast.error(`Update failed: ${e.message}`);
    } finally { setBusy(''); }
  };

  const removeLead = async (id) => {
    if (!window.confirm('Delete this lead permanently?')) return;
    setBusy(id + ':del');
    try {
      await api.adminInfraQuoteDelete(id);
      toast.success('Lead deleted');
      setSelected(null);
      await refresh();
    } catch (e) {
      toast.error(`Delete failed: ${e.message}`);
    } finally { setBusy(''); }
  };

  // Count by status
  const counts = rows.reduce((acc, r) => { const s = r.status || 'new'; acc[s] = (acc[s] || 0) + 1; return acc; }, {});
  const totalRevenue = rows.filter(r => r.status === 'paid').reduce((s, r) => s + Number(r.quoted_amount || 0), 0);

  return (
    <Section
      kicker="// LEADS · INFRA"
      title="INFRASTRUCTURE QUOTE REQUESTS"
      actions={
        <div className="flex items-center gap-2 flex-wrap">
          <div className="eh-mono text-[10px] tracking-widest opacity-60 mr-2">
            <span className="text-[var(--eh-green)]">{rows.length}</span> total
            {totalRevenue > 0 && <span className="ml-2">· <span className="text-[#ffd34d]">₹{totalRevenue.toLocaleString()}</span> paid</span>}
          </div>
          <select value={status} onChange={e => setStatus(e.target.value)} className="eh-input text-xs" data-testid="infra-leads-filter">
            <option value="">All statuses</option>
            {Object.entries(STATUS).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
          </select>
          <button onClick={refresh} className="eh-btn-ghost text-xs" data-testid="infra-leads-refresh">
            <RefreshCcw size={12} className={loading ? 'animate-spin' : ''} /> REFRESH
          </button>
        </div>
      }
    >
      {/* KPI strip */}
      <div className="grid grid-cols-3 sm:grid-cols-6 gap-2 mb-5">
        {Object.entries(STATUS).map(([k, v]) => (
          <button
            key={k}
            onClick={() => setStatus(status === k ? '' : k)}
            className="eh-panel p-3 text-left hover:-translate-y-0.5 transition-all"
            style={{ borderColor: status === k ? v.color : `${v.color}33` }}
            data-testid={`infra-leads-tile-${k}`}
          >
            <div className="eh-mono text-[9.5px] tracking-widest opacity-70 mb-1" style={{ color: v.color }}>{v.label}</div>
            <div className="eh-display text-2xl font-black" style={{ color: v.color }}>{counts[k] || 0}</div>
          </button>
        ))}
      </div>

      {/* Body — list on the left, detail on the right on desktop */}
      <div className={`grid ${selected ? 'lg:grid-cols-2' : ''} gap-3`}>
        <div className={`${selected ? 'hidden lg:block' : ''} eh-panel p-0 overflow-hidden`}>
          <div className="max-h-[68vh] overflow-y-auto eh-scroll">
            {loading && <div className="p-8 text-center opacity-60 eh-mono text-xs"><Loader2 size={14} className="inline animate-spin mr-2" />Loading leads…</div>}
            {!loading && rows.length === 0 && (
              <div className="p-10 text-center">
                <Filter size={20} className="mx-auto mb-3 opacity-40" />
                <div className="eh-mono text-xs opacity-60">No leads {status ? `with status "${STATUS[status]?.label || status}"` : 'yet'}.</div>
                <div className="mt-1 opacity-40 text-[11px]">Leads land here when someone clicks REQUEST QUOTE on /hacking-servers.</div>
              </div>
            )}
            {rows.map(r => {
              const stat = STATUS[r.status || 'new'] || STATUS.new;
              const CIcon = CHANNEL_ICON[r.contact_channel] || Mail;
              return (
                <div
                  key={r._id}
                  onClick={() => setSelected(r)}
                  data-testid={`infra-lead-row-${r._id}`}
                  className={`p-4 border-b border-[var(--eh-border)] cursor-pointer hover:bg-white/[.03] transition-colors ${selected?._id === r._id ? 'bg-white/[.05]' : ''}`}
                >
                  <div className="flex items-start gap-3">
                    <div className="w-9 h-9 rounded-lg grid place-items-center shrink-0" style={{ background: `${stat.color}18`, border: `1px solid ${stat.color}55`, color: stat.color }}>
                      <CIcon size={14} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-0.5">
                        <div className="text-[13.5px] font-bold truncate" style={{ fontFamily: 'Inter,sans-serif' }}>
                          {r.service_name || r.service_id || 'Untitled request'}
                        </div>
                        <span className="eh-mono text-[9px] px-1.5 py-0.5 rounded font-bold shrink-0" style={{ background: `${stat.color}22`, color: stat.color, border: `1px solid ${stat.color}55` }}>
                          {stat.label}
                        </span>
                      </div>
                      <div className="eh-mono text-[10.5px] opacity-70 truncate">{r.contact} · <span className="opacity-60">{r.contact_channel}</span></div>
                      {r.note && <div className="text-[11.5px] opacity-70 mt-1 line-clamp-2" style={{ fontFamily: 'Inter,sans-serif' }}>{r.note}</div>}
                      <div className="flex items-center gap-2 mt-1.5 eh-mono text-[10px] opacity-60">
                        {r.budget_hint && <span><DollarSign size={9} className="inline" /> {r.budget_hint}</span>}
                        {r.user_email && <span><User size={9} className="inline" /> {r.user_email}</span>}
                        {!r.user_email && <span>guest</span>}
                        <span>· {timeAgo(r.created_at)} ago</span>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Detail pane */}
        {selected && (
          <div className="eh-panel p-5" data-testid="infra-lead-detail">
            <div className="lg:hidden mb-3">
              <button onClick={() => setSelected(null)} className="eh-mono text-[10px] tracking-widest opacity-70 hover:text-[var(--eh-green)]">← BACK TO LIST</button>
            </div>
            <div className="flex items-start justify-between mb-4">
              <div className="min-w-0">
                <div className="eh-kicker mb-1">// LEAD DETAIL</div>
                <div className="text-lg font-black truncate" style={{ fontFamily: 'Inter,sans-serif' }}>{selected.service_name || 'Untitled'}</div>
                <div className="eh-mono text-[10px] opacity-60">{selected.service_id || '—'} · {timeAgo(selected.created_at)} ago</div>
              </div>
              <button onClick={() => removeLead(selected._id)} disabled={busy.startsWith(selected._id)} className="eh-btn-ghost text-[10px] disabled:opacity-50" style={{ color: '#ff5078', borderColor: 'rgba(255,80,120,.4)' }}>
                <Trash2 size={11} /> DELETE
              </button>
            </div>

            {/* Contact */}
            <div className="grid grid-cols-2 gap-2 mb-4">
              <div className="p-3 rounded-lg border border-[var(--eh-border)]">
                <div className="eh-mono text-[9.5px] tracking-widest opacity-60 mb-1">CONTACT · {selected.contact_channel?.toUpperCase()}</div>
                <a
                  href={contactHref(selected.contact_channel, selected.contact) || '#'}
                  target={selected.contact_channel === 'email' ? undefined : '_blank'}
                  rel="noreferrer"
                  className="text-[13px] font-bold eh-neon truncate block" style={{ fontFamily: 'Inter,sans-serif' }}
                  data-testid="infra-lead-contact"
                >
                  {selected.contact} <ExternalLink size={10} className="inline opacity-60" />
                </a>
              </div>
              <div className="p-3 rounded-lg border border-[var(--eh-border)]">
                <div className="eh-mono text-[9.5px] tracking-widest opacity-60 mb-1">BUDGET</div>
                <div className="text-[13px] font-bold" style={{ color: '#ffd34d' }}>{selected.budget_hint || '—'}</div>
              </div>
            </div>

            {selected.user_email && (
              <div className="mb-4 p-3 rounded-lg border border-[var(--eh-border)]">
                <div className="eh-mono text-[9.5px] tracking-widest opacity-60 mb-1">SITE USER</div>
                <div className="text-[13px]"><User size={11} className="inline mr-1 opacity-70" /> {selected.user_email}</div>
              </div>
            )}

            {selected.note && (
              <div className="mb-4">
                <div className="eh-mono text-[9.5px] tracking-widest opacity-60 mb-1.5">// NOTE FROM CLIENT</div>
                <div className="p-3 rounded-lg border text-[13px] leading-relaxed" style={{ borderColor: 'rgba(0,255,157,.25)', background: 'rgba(0,255,157,.04)', fontFamily: 'Inter,sans-serif' }}>
                  {selected.note}
                </div>
              </div>
            )}

            {/* Status Kanban */}
            <div className="mb-4">
              <div className="eh-mono text-[9.5px] tracking-widest opacity-60 mb-2">// MOVE STATUS</div>
              <div className="grid grid-cols-3 gap-1.5">
                {Object.entries(STATUS).map(([k, v]) => {
                  const isCurrent = (selected.status || 'new') === k;
                  const isBusy = busy === selected._id + ':' + k;
                  return (
                    <button
                      key={k}
                      onClick={() => updateStatus(selected._id, k)}
                      disabled={isCurrent || !!busy}
                      className="p-2 rounded border text-[10px] eh-mono tracking-widest font-bold transition-all disabled:opacity-40"
                      style={{
                        background: isCurrent ? v.color : `${v.color}12`,
                        color: isCurrent ? '#0a0703' : v.color,
                        borderColor: `${v.color}55`,
                      }}
                      data-testid={`infra-lead-status-${k}`}
                    >
                      {isBusy ? '…' : v.label}
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Quick actions */}
            <div className="grid grid-cols-2 gap-2">
              <a href={contactHref(selected.contact_channel, selected.contact) || '#'} target={selected.contact_channel === 'email' ? undefined : '_blank'} rel="noreferrer" className="eh-btn-primary text-[10px] text-center flex items-center justify-center gap-1.5">
                <Zap size={11} /> REACH OUT
              </a>
              <button
                onClick={() => updateStatus(selected._id, selected.status === 'contacted' ? 'quoted' : 'contacted')}
                disabled={!!busy}
                className="eh-btn-ghost text-[10px]"
                data-testid="infra-lead-quick-advance"
              >
                → MARK {selected.status === 'contacted' ? 'QUOTED' : 'CONTACTED'}
              </button>
            </div>
          </div>
        )}
      </div>
    </Section>
  );
};

export default InfraLeads;
